import { BrowserModule, Title } from '@angular/platform-browser';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { Router, RouterModule } from '@angular/router';

import { AppComponent } from './app.component';
import { routes } from './app.routes';
import { AuthGuard } from './guards/auth.guard';
import { CommunityRedirectGuard } from './guards/community-redirect.guard';
import { environment } from '../environments/environment';
import { OktaAuthModule, OKTA_CONFIG } from '@okta/okta-angular';
import OktaAuth, { toRelativeUrl } from '@okta/okta-auth-js';
import { oktaAuth } from './okta.config';

// Store
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { reducers } from './store/reducers';
import { effects } from './store/effects';

// Modules
import { SharedModule } from './components/shared/shared.module';
import { LayoutModule } from './components/layout/layout.module';
import { NewsModule } from './modules/news/news.module';
import { SupportModule } from './components/support/support.module';
import { SearchModule } from './modules/search/search.module';
import { PartnersModule } from './modules/partners/partners.module';

// Components
import { IconspriteComponent } from './components/iconsprite/iconsprite.component';
import { CommunityComponent } from './components/community/community.component';
import { AuthCallbackComponent } from './components/auth-callback/auth-callback.component';

// Services
import { AppService } from './services/app.service';
import { AuthService } from './services/auth.service';
import { IconspriteService } from './services/iconsprite.service';
import { TokenService } from './services/token.service';
import { HippoContentService } from './services/hippo-content.service';

// Interceptors
import { TokenInterceptor } from './interceptors/token.interceptor';
import { ResponseInterceptor } from './interceptors/response.interceptor';
import { TimeoutInterceptor } from './interceptors/timeout.interceptor';
import { UserManagementService } from './services/user-management.service';
import { LoginRedirectGuard } from './guards/login-redirect.guard';
import { SignoutGuard } from './guards/signout.guard';
import { IntuoComponent } from './components/pages/intuo/intuo.component';
import { SalesComponent } from './components/pages/sales/sales.component';
import { Resources4uComponent } from './components/pages/resources4u/resources4u.component';
import { Support4UComponent } from './components/support4u/support4u.component';
import { DownloadComponent } from './components/download/download.component';
import { MarketplaceComponent } from './components/martketplace/marketplace.component';

@NgModule({
    declarations: [
        AppComponent,
        CommunityComponent,
        AuthCallbackComponent,
        IconspriteComponent,
        IntuoComponent,
        SalesComponent,
        Resources4uComponent,
        MarketplaceComponent,
        Support4UComponent,
        DownloadComponent,
        MarketplaceComponent
    ],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA,
        NO_ERRORS_SCHEMA,
    ],
    imports: [
        BrowserModule,
        SharedModule,
        HttpClientModule,
        LayoutModule,
        NewsModule,
        SupportModule,
        PartnersModule,
        SearchModule,
        RouterModule.forRoot(routes, { paramsInheritanceStrategy: 'always' }),
        OktaAuthModule,
        StoreModule.forRoot(reducers),
        EffectsModule.forRoot(effects),
        // disable StoreDevTools in production
        StoreDevtoolsModule.instrument({
            maxAge: 25, // Retains last 25 states
            logOnly: environment.production, // Restrict extension to log-only mode
        }),
    ],
    providers: [
        AppService,
        AuthGuard,
        CommunityRedirectGuard,
        LoginRedirectGuard,
        SignoutGuard,
        AuthService,
        HippoContentService,
        IconspriteService,
        TokenService,
        Title,
        UserManagementService,
        {
            provide: OKTA_CONFIG,
            useFactory: (router: Router) => ({
                oktaAuth,
                // Return the user to where they were before login (Angular navigation, not a full reload).
                restoreOriginalUri: async (_oktaAuth: OktaAuth, originalUri: string) => {
                    await router.navigateByUrl(toRelativeUrl(originalUri || '/', window.location.origin));
                }
            }),
            deps: [Router]
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ResponseInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TimeoutInterceptor,
            multi: true
        }
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }
